<script setup lang="ts">
import { computed } from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import Feedback from '@/components/home/insights/feedback/feedback.vue';
import { provideInsightSources } from '@/components/home/insights/use-insight-sources';
import {
  provideInsightTracking,
  useInsightPageName,
} from '@/components/home/insights/use-insight-tracking';
import { insightAxisPresentations } from '@/components/insights/insight-display';
import InsightSaveButton from '@/components/insights/insight-save-button.vue';
import InsightTopicSummary from '@/components/insights/insight-topic-summary.vue';
import { toSavedInsightHeader } from '@/components/insights/saved/saved-insight-display';
import type {
  InsightHintTrackingContext,
  InsightSavedHintDetail,
} from '@/types/insights';
import { featureFlags } from '@/featureFlags';

interface Props {
  detail: InsightSavedHintDetail;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  unsaved: [];
}>();

const header = computed(() => toSavedInsightHeader(props.detail));
const axisIcon = computed(
  () => insightAxisPresentations[props.detail.axis].icon,
);
const isTopicHintPair = featureFlags.ANDEV_10719_INSIGHT_TOPIC_HINT_PAIR;

const pageName = useInsightPageName();

// 元のトピックのポップオーバーは body の provide の外にあるため、
// ヘッダーでも出典と計測の文脈を用意する。
if (isTopicHintPair) {
  provideInsightSources(() => props.detail.sources);
  provideInsightTracking({
    pageName,
    insightId: props.detail.insight_id,
    axisId: props.detail.axis_id,
    axisName: props.detail.axis,
  });
}

const tracking = computed<InsightHintTrackingContext>(() => ({
  pageName,
  insightId: props.detail.insight_id,
  axisId: props.detail.axis_id,
  axisName: props.detail.axis,
  hintId: props.detail.hint.id,
}));

const onSettled = (saved: boolean) => {
  if (saved) return;
  emit('unsaved');
};
</script>

<template>
  <div class="saved-insight-detail-header" :class="{ paired: isTopicHintPair }">
    <div class="heading">
      <div v-if="isTopicHintPair" class="title-row">
        <div class="c-title-medium--m title">
          <DgrIcon name="tips" :keep-fill="false" />
          {{ header.title }}
        </div>
        <VTooltip
          v-if="detail.topic"
          theme="popover"
          placement="bottom-start"
          popper-class="saved-insight-topic-popper"
          class="topic-chip-container"
        >
          <span
            class="c-outlineTag c-outlineTag--small c-outlineBtn--secondary c-title-medium--xs topic-chip"
          >
            元のトピック
          </span>
          <template #popper>
            <InsightTopicSummary
              class="topic-popover"
              :topic="detail.topic"
              :axis="detail.axis"
              source-variant="gray"
            />
          </template>
        </VTooltip>
      </div>
      <div v-else class="c-title-medium--m title">
        <DgrIcon name="tips" size="small" :keep-fill="false" />
        {{ header.title }}
      </div>
      <div class="c-text-regular--s meta">
        <span>{{ header.dateLabel }}</span>
        <template v-if="!isTopicHintPair">
          <span>・</span>
          <span class="axis">
            関心領域：
            <span class="axis-name">
              <DgrIcon :name="axisIcon" size="small" />
              {{ header.axisLabel }}
            </span>
          </span>
        </template>
      </div>
    </div>

    <div class="actions">
      <InsightSaveButton
        :hint-uuid="detail.hint.uuid"
        :initial-saved="detail.hint.saved"
        :tracking="tracking"
        @settled="onSettled"
      />
      <Feedback :hint-uuid="detail.hint.uuid" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.topic-popover {
  gap: 4px;
}

.saved-insight-detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  box-sizing: border-box;
  padding: 16px 24px;
  background-color: $color-white;
  border-bottom: 1px solid $color-gray400;

  .heading {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    min-width: 0;

    .title {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .meta {
      display: flex;
      align-items: center;
      gap: 4px;
      color: $color-gray800;

      .axis,
      .axis-name {
        display: flex;
        align-items: center;
      }

      .axis-name {
        gap: 2px;
      }
    }

    .title-row {
      display: flex;
      align-items: center;
      gap: 8px;

      // DgrIcon のサイズプリセットに 20px が無いため上書きする。
      .title .icon-box {
        width: 20px;
        height: 20px;
      }
    }

    .topic-chip {
      margin-left: 0;
      color: $color-gray800;
      cursor: default;
    }
  }

  .actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }

  &:not(.paired) .actions :deep(button) {
    border: 1px solid $color-gray400;
  }
}
</style>
